import { useMemo } from 'react';

import keyBy from 'lodash/keyBy';

import {
  ConsoleRules,
  mergeRulesWithOverrides,
  Overrides,
  ruleBuilder,
  UseRules,
} from '@cast/shared-ability-data-access';
import { FeatureFlagResolved, GetSettingsResponse } from '@cast/types';

import { useUserAbilityOverrides } from './user-ability-overrides.state';
import { useAccessControl } from './AccessControlProvider';
import { useAbilityOverrides } from './dev-tools/ability-overrides-state';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

// Merge user and dev overrides - dev overrides take precedence
const mergeOverrides = (
  userOverrides: Overrides,
  devOverrides: Overrides
): Overrides => {
  const result: Overrides = { ...userOverrides };

  for (const subject of Object.keys(devOverrides) as Array<keyof Overrides>) {
    if (devOverrides[subject]) {
      result[subject] = {
        ...result[subject],
        ...devOverrides[subject],
      };
    }
  }

  return result;
};

export const useRulesWithOverrides = (
  flags: FeatureFlagResolved[],
  subjectId?: string,
  orgSettings?: GetSettingsResponse
): UseRules => {
  const { currentOrganization, organizations } = useOrganizations();
  const { isLoading, subjectAccessData, error } = useAccessControl({
    subjectId,
    // false to not cause cluster fetching
    excludeOrphanedClusters: false,
  });

  const rules = useMemo(() => {
    const result: ConsoleRules[] = [];
    if (subjectAccessData && subjectId && currentOrganization?.id) {
      result.push(
        ...ruleBuilder({
          subjectId,
          accessData: subjectAccessData,
          flags,
          organizationMap: keyBy(organizations, 'id'),
          orgSettings,
        })
      );
    }
    return result;
  }, [
    subjectAccessData,
    subjectId,
    currentOrganization?.id,
    flags,
    organizations,
    orgSettings,
  ]);

  // Developer overrides (DevToolbox)
  const devOverrides = useAbilityOverrides(({ overrides }) => overrides);
  const globalOverride = useAbilityOverrides(
    ({ globalOverride }) => globalOverride
  );

  // User overrides (What user is allowed to override from the UI)
  const userOverrides = useUserAbilityOverrides(({ overrides }) => overrides);

  // Merge: user overrides first, then dev overrides on top (dev takes precedence)
  const combinedOverrides = useMemo(
    () => mergeOverrides(userOverrides, devOverrides),
    [userOverrides, devOverrides]
  );

  const rulesWithOverrides = useMemo(
    () =>
      rules
        ? mergeRulesWithOverrides({
            rules,
            overrides: combinedOverrides,
            globalOverride,
          })
        : undefined,
    [rules, combinedOverrides, globalOverride]
  );

  return {
    isLoading,
    rules: rulesWithOverrides,
    rulesOrig: rules,
    error,
  };
};
